import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { TrashIcon } from '@heroicons/react/24/outline';

interface DeletePaymentModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  onConfirm: () => void;
  isDeleting?: boolean;
  paymentDetails?: {
    amount: number;
    date: string;
    paymentType: string;
    name?: string;
  };
}

export default function DeletePaymentModal({
  open,
  setOpen,
  onConfirm,
  isDeleting = false,
  paymentDetails,
}: DeletePaymentModalProps) {
  const formatDateOnly = (dateString: string) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString.endsWith('Z') ? dateString : dateString + 'Z');
      if (isNaN(date.getTime())) return '';
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
    } catch {
      return '';
    }
  };

  const formatPaymentMethod = (paymentType: string) => {
    if (!paymentType) return '';
    const paymentMethodMap: Record<string, string> = {
      CARD: 'Card',
      APPLE_PAY: 'Apple Pay',
      GOOGLE_PAY: 'Google Pay',
      PAYPAL: 'PayPal',
      PAY_BY_BANK: 'ACH',
      VENMO: 'Venmo',
      CASH: 'Cash',
      CHECK: 'Check',
      CRYPTO: 'Crypto',
      IN_KIND: 'In Kind',
      STOCK: 'Stock',
      DONOR_ADVISED_FUND: 'Donor Advised Fund',
      OTHER: 'Other',
      FREE: 'Free',
    };
    
    if (paymentMethodMap[paymentType]) {
      return paymentMethodMap[paymentType];
    }
    
    // Fallback: convert snake_case or UPPERCASE to Title Case
    return paymentType
      .replace(/_/g, ' ')
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent showX={false} className="max-w-[500px] w-[calc(100vw-2rem)]">
        <div className="sm:flex sm:items-start">
          <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-status-red-bg sm:mx-0 sm:h-10 sm:w-10">
            <TrashIcon className="size-6 text-status-red-text" />
          </div>

          <div className="ml-4 mt-0 flex-grow">
            <DialogHeader>
              <DialogTitle className="text-lg text-ink-muted">Delete Payment</DialogTitle>
            </DialogHeader>
            <div className="mt-2">
              <p className="text-sm text-ink-muted">
                Are you sure you want to delete this payment? This action cannot be undone.
              </p>
              {paymentDetails && (
                <div className="mt-3 rounded-md border border-edge bg-paper-muted px-3 py-2">
                  <p className="text-sm text-ink-secondary">
                    {formatDateOnly(paymentDetails.date)}
                    {paymentDetails.name && ` • ${paymentDetails.name}`}
                    {` • $${paymentDetails.amount.toFixed(2)} • ${formatPaymentMethod(paymentDetails.paymentType)}`}
                  </p>
                </div>
              )}
              <Separator className="mb-0 mt-4 bg-paper-inset text-edge" />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="neutral"
            size="workspace"
            onClick={() => setOpen(false)}
            disabled={isDeleting}
          >
            Cancel
          </Button>

          <Button
            type="button"
            variant="dangerConfirm"
            size="workspace"
            onClick={onConfirm}
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
